import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import styles from "./HeroCarousel.module.css";
import Hero1 from "../../assets/banner6.png";
import Hero2 from "../../assets/banner5.png";
import Hero3 from "../../assets/banner7.png";
import Hero4 from "../../assets/banner6.png";

const slides = [
  { title: "A visionary leader shaping an inclusive society", image: Hero1 },
  { title: "A forward-thinking entrepreneur building businesses with people at the heart", image: Hero2 },
  { title: "A devoted family man, grounded in values and compassion", image: Hero3 },
  { title: "A passionate sports advocate inspiring unity, growth, and social change", image: Hero4 },
];

const AUTOPLAY_MS = 5200;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced] = useState(prefersReducedMotion);

  const copyRef = useRef(null);

  const goTo = useCallback((index) => {
    setCurrent(((index % slides.length) + slides.length) % slides.length);
  }, []);
  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // Autoplay. Held while the visitor is hovering or tabbing through the hero,
  // and switched off entirely for reduced motion - the controls still work.
  useEffect(() => {
    if (paused || reduced) return undefined;
    const timer = window.setInterval(
      () => setCurrent((prev) => (prev + 1) % slides.length),
      AUTOPLAY_MS
    );
    return () => window.clearInterval(timer);
  }, [paused, reduced]);

  // Arrow keys move between slides.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") prev();
    };
    const node = copyRef.current?.closest("section");
    node?.addEventListener("keydown", onKeyDown);
    return () => node?.removeEventListener("keydown", onKeyDown);
  }, [next, prev]);

  // Re-animate the copy each time the slide changes.
  useLayoutEffect(() => {
    if (reduced) return undefined;
    const scope = copyRef.current;
    if (!scope) return undefined;

    const ctx = gsap.context(() => {
      gsap.from("[data-copy]", {
        opacity: 0,
        y: 26,
        duration: 0.7,
        stagger: 0.07,
        ease: "power3.out",
      });
    }, scope);
    return () => ctx.revert();
  }, [current, reduced]);

  return (
    <section
      className={styles.hero}
      aria-roledescription="carousel"
      aria-label="Jose Charles Martin"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* ---- Stage: the slide images, cross-faded ---- */}
      <div className={styles.stage}>
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`${styles.slide} ${index === current ? styles.active : ""}`}
            aria-hidden={index !== current}
          >
            <img
              src={slide.image}
              alt=""
              className={styles.slideImg}
              /* Only the first slide is above the fold on load. */
              loading={index === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
        <span className={styles.stageWash} aria-hidden="true" />
      </div>

      {/* ---- Copy column ---- */}
      <div className={styles.copy} ref={copyRef}>
        <div className={styles.copyInner}>
          <span className={styles.counter} data-copy>
            <span className={styles.counterNow}>
              {String(current + 1).padStart(2, "0")}
            </span>
            <span className={styles.counterRule} aria-hidden="true" />
            <span className={styles.counterAll}>
              {String(slides.length).padStart(2, "0")}
            </span>
          </span>

          <h1
            className={styles.title}
            data-copy
            aria-live="polite"
            key={current}
          >
            {slides[current].title}
          </h1>

          {/* Controls live in the copy column's normal flow, so they cannot
              overlap the headline and are centred on the column by default -
              the two problems the old absolutely-positioned group had. */}
          <div className={styles.controls} data-copy>
            <div className={styles.progress} role="tablist" aria-label="Slides">
              {slides.map((slide, index) => (
                <button
                  key={index}
                  type="button"
                  role="tab"
                  aria-selected={index === current}
                  aria-label={slide.title}
                  className={`${styles.bar} ${index === current ? styles.barActive : ""}`}
                  onClick={() => goTo(index)}
                >
                  <span
                    className={styles.barFill}
                    style={
                      index === current && !reduced
                        ? {
                            animationDuration: `${AUTOPLAY_MS}ms`,
                            animationPlayState: paused ? "paused" : "running",
                          }
                        : undefined
                    }
                  />
                </button>
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
